import { useContext, useEffect, useMemo, useState } from "react";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import { Input } from "semantic-ui-react";
import useDebounce from "../../../core/UseDebounce";
import { LookupsContext } from "../../../lookups/LookupFactory";
import WatershedManagementGroup from "../../../models/lutModels/WatershedManagementGroup";
import ReceivingWaters from "../../../models/ReceivingWaters";
import { MultiSelectComponent } from "../../inputs/multiSelectComponent/MultiSelectComponent";
import "./FilterComponent.scss"
import { BmpClassId } from "../../../models/lutModels/LutBmpClass";

export class ManageProjectFilter {
    statuses: string[] = [];
    bmpClasses: string[] = [];
    bmpTypes: string[] = [];
    watershedManagementGroups: string[] = [];
    receivingWaters: string[] = [];
    keywords: string[] = [];
    minimumCompletionDate?: Date = null;
    maximumCompletionDate?: Date = null;
    minimumCreationDate?: Date = null;
    maximumCreationDate?: Date = null;
    minimumLastModificationDate?: Date = null;
    maximumLastModificationDate?: Date = null;
    jurisdictions: string[];
}

export interface IFilterComponentProps {
    onChange: (filter: ManageProjectFilter) => void;
    currentJurisdictionId: number;
}

export const FilterComponent = (props: IFilterComponentProps) => {
    const [filter, setFilter] = useState<ManageProjectFilter>(new ManageProjectFilter());
    const [inputReset, setInputReset] = useState<Date>(new Date());
    const [lookups] = useContext(LookupsContext);

    function handleDateChange(e: any, dateValue: any) {
        let { name, value } = dateValue ?? e.target;
        setFilter({
            ...filter,
            [name]: value,
        });
    };

    function filterMaxDate(maxDate?: Date | null) {
        return (date: Date): boolean => {
            if (!maxDate) return true;

            const max = new Date(maxDate);
            max.setHours(0, 0, 0, 0);

            const current = new Date(date);
            current.setHours(0, 0, 0, 0);

            return current <= max;
        };
    }

    function filterMinDate(minDate?: Date | null) {
        return (date: Date): boolean => {
            if (!minDate) return true;

            const min = new Date(minDate);
            min.setHours(0, 0, 0, 0);

            const current = new Date(date);
            current.setHours(0, 0, 0, 0);

            return current >= min;
        };
    }

    const handleMultiselectChange = (name: string, list: any[], _: any) => {
        setFilter({
            ...filter,
            [name]: list.map(l => l.internalName)
        });
    };

    const [keywords, setKeywords] = useState<string[]>();

    const handleKeywordChange = (_: any, data: any) => {
        setKeywords(data.value.split(" ").filter((kw: string) => kw !== ""));
    }

    const debouncedKeywords = useDebounce(keywords, 500);

    useEffect(() => {
        setFilter((prevFilter) => ({
            ...prevFilter,
            keywords: debouncedKeywords
        }));
    }, [debouncedKeywords]);

    const onChange = useMemo(() => props.onChange, [props.onChange]);

    useEffect(() => {
        onChange(filter);
    }, [filter]);

    useEffect(() => {
        //reset filters on jurisdiction change
        setFilter(new ManageProjectFilter());
        setInputReset(new Date());
    }, [props.currentJurisdictionId]);

    const [watershedGroups, setWatershedGroups] = useState<WatershedManagementGroup[]>([]);
    useEffect(() => {
        if (lookups?.lutWatershedManagementGroup == null) {
            return;
        }
        var groupIds = lookups.jurisdictionGroups
            .filter(jg => jg.jurisdictionId === props.currentJurisdictionId)
            .map(jg => jg.watershedManagementGroupId);

        if (props.currentJurisdictionId === 1087) {
            groupIds = lookups.lutWatershedManagementGroup.map(g => g.id);
        }

        const groups = lookups.lutWatershedManagementGroup
            .filter(g => groupIds.some(gi => gi === g.id));

        setWatershedGroups(groups);
    }, [props.currentJurisdictionId, lookups.jurisdictionGroups, lookups.lutWatershedManagementGroup]);

    const receivingWaters: ReceivingWaters[] = useMemo(() => {
        return lookups.lutReceivingWaters
            .filter(rw => rw.wsGroupRWaterJurisdictionMapping
                .some(mapping => mapping.jurisdictionId === props.currentJurisdictionId));
    }, [props.currentJurisdictionId, lookups.lutReceivingWaters])

    return (
        lookups != null &&
        <div className="filter-container" key={`${inputReset}`}>
            <div className="outer-wrapper">
                <div className="filter-wrapper">
                    <MultiSelectComponent
                        name="statuses"
                        label="Project Status"
                        options={lookups.lutBmpStatus}
                        onChange={handleMultiselectChange}
                        placeholder="Select from list"
                        displayValue="displayName" />
                    <MultiSelectComponent
                        name="bmpClasses"
                        label="Project Type"
                        options={lookups.lutBmpClass.filter(bmpClass => bmpClass.id !== BmpClassId.LowFlowDiversion)}
                        onChange={handleMultiselectChange}
                        placeholder="Select from list"
                        displayValue="displayName" />
                    <MultiSelectComponent
                        name="watershedManagementGroups"
                        label="Watershed Group"
                        options={watershedGroups}
                        onChange={handleMultiselectChange}
                        placeholder="Select from List"
                        displayValue="displayName" />
                </div>
                <div className="filter-wrapper">
                    <MultiSelectComponent
                        name="receivingWaters"
                        label="Receiving Water / Subwatershed"
                        options={receivingWaters}
                        onChange={handleMultiselectChange}
                        placeholder="Select from List"
                        displayValue="displayName" />
                    <MultiSelectComponent
                        name="bmpTypes"
                        label="BMP Type"
                        options={lookups.lutBmpType}
                        onChange={handleMultiselectChange}
                        placeholder="Select from List"
                        displayValue="displayName" />
                    <div>
                        <div className="basic-input-label-wrapper">
                            <label className="basic-input-label">Keywords</label>
                        </div>
                        <Input
                            onChange={handleKeywordChange}
                            name="keywords"
                            focus
                            placeholder=""
                            icon="search" />
                    </div>
                </div>
            </div>
            <div className="dates-wrapper">
                <div>
                    <div className="basic-input-label-wrapper">
                        <label className="basic-input-label">Completion Date Between</label>
                    </div>
                    <div className="date-input-wrapper">
                        <SemanticDatepicker
                            name="minimumCompletionDate"
                            onChange={handleDateChange}
                            placeholder="Start Date"
                            filterDate={(filterMaxDate(filter.maximumCompletionDate))}
                        />
                        <SemanticDatepicker
                            name="maximumCompletionDate"
                            onChange={handleDateChange}
                            placeholder="End Date"
                            filterDate={filterMinDate(filter.minimumCompletionDate)} />
                    </div>
                </div>
                <div>
                    <div className="basic-input-label-wrapper">
                        <label className="basic-input-label">Creation Date Between</label>
                    </div>
                    <div className="date-input-wrapper">
                        <SemanticDatepicker
                            name="minimumCreationDate"
                            onChange={handleDateChange}
                            placeholder="Start Date"
                            filterDate={(filterMaxDate(filter.maximumCreationDate))}
                        />
                        <SemanticDatepicker
                            name="maximumCreationDate"
                            onChange={handleDateChange}
                            placeholder="End Date"
                            filterDate={(filterMinDate(filter.minimumCreationDate))}
                        />
                    </div>
                </div>
                <div>
                    <div className="basic-input-label-wrapper">
                        <label className="basic-input-label">Modification Date Between</label>
                    </div>
                    <div className="date-input-wrapper">
                        <SemanticDatepicker
                            name="minimumLastModificationDate"
                            onChange={handleDateChange}
                            placeholder="Start Date"
                            filterDate={(filterMaxDate(filter.maximumLastModificationDate))}
                        />
                        <SemanticDatepicker
                            name="maximumLastModificationDate"
                            onChange={handleDateChange}
                            placeholder="End Date"
                            filterDate={(filterMinDate(filter.minimumLastModificationDate))}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
